/**
 * Usage without sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w" />
 *
 * Usage with sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *     sizes="(max-width: 600px) 100vw, 50vw"
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w"
 *     sizes="(max-width: 600px) 100vw, 50vw" />
 *
 *   // This means that below 600px image will take as many pixels there are available on current
 *   // viewport width (100vw) - and above that image will only take 50% of the page width.
 *   // Browser decides which image it will fetch based on current screen size.
 *
 * NOTE: for all the possible image variant names and their respective
 * sizes, see the API documentation.
 */

import React from 'react';
import { arrayOf, oneOfType, string, bool } from 'prop-types';
import classNames from 'classnames';
import { propTypes } from '../../util/types';

import { AdvancedImage, responsive } from '@cloudinary/react';
import { fit } from '@cloudinary/url-gen/actions/resize';
import { H4 } from '../Heading/Heading';
import IconClose from '../IconClose/IconClose';
import cloudinary, {
  buildCloudinaryImageSources,
  getUrlVariants,
  withCloudinaryJpgFormat,
} from '../../util/cloudinary';
import css from './ResponsiveImage.module.css';


const ResponsiveImage = props => {
  const {
    className,
    rootClassName,
    alt,
    noImageMessage,
    image,
    variants,
    dimensions,
    abbreviatedName = '',
    aspectHeight = 1,
    aspectWidth = 1,
    renderCloudinaryImage,
    // Image optimization props
    isCritical = false,
    loading = 'lazy',
    decoding = 'async',
    fetchPriority = 'auto',
    ...rest
  } = props;


  const classes = classNames(rootClassName || css.root, className);

  // Render image file
  if (!renderCloudinaryImage && image && ( typeof File !== 'undefined' && image instanceof File)) {
    return (
      <img
        src={URL.createObjectURL(image)}
        alt={alt}
        loading={isCritical ? 'eager' : loading}
        decoding={decoding}
        fetchpriority={isCritical ? 'high' : fetchPriority}
        {...rest}
      />
    );
  };

  // Render Cloudinary Image Responsively
  if (renderCloudinaryImage && renderCloudinaryImage.secure_url) {

    const defaultOptions = {
      format: 'auto',
      quality: 'auto:eco',
      dpr: 'auto',
      crop: 'fit'
    };

    const {
      secure_url,
      alt = 'image',
      variantsToRender = ['listing-card'],
      defaultVariant = 'listing-card',
      style = {},
      cloudinaryOptions = {},
      cloudinaryClassName = '',
      // Cloudinary-specific options (format, quality, dpr, etc.)
    } = renderCloudinaryImage;

    const mergedOptions = { ...defaultOptions, ...cloudinaryOptions }

    // Get URL variants for the provided image with transformation options
    const secureUrl = secure_url;

    // WebP sources (the app's default `format: 'auto'` resolves to WebP on this Cloudinary
    // account regardless of the Accept header) plus JPEG sources for the fallback <img>.
    // Both come from buildCloudinaryImageSources so that a <link rel="preload"> built from the
    // same helper requests byte-identical URLs — see util/cloudinary.js for why that matters.
    const sourceArgs = { secureUrl, variantsToRender, defaultVariant, options: mergedOptions };
    const { srcset, sizes: sizesAttr, defaultUrl } = buildCloudinaryImageSources(sourceArgs);
    const { srcset: jpgSrcset, defaultUrl: jpgUrl } = buildCloudinaryImageSources({
      ...sourceArgs,
      format: 'jpg',
    });

    // Default variant URLs. The <img> fallback is served as JPEG so old Safari (11.1/12.1/13.1
    // — none decode WebP, and Cloudinary `f_auto` serves WebP to them anyway) gets a decodable
    // image; modern browsers pick the WebP <source> above and never fetch the JPEG.
    const jpgDefaultUrl = jpgUrl || defaultUrl;

    // Default styles for responsiveness
    const defaultStyles = {
      height: '100%',
      width: '100%',
    };

    // Merge user-defined styles with default styles
    const mergedStyles = { ...defaultStyles, ...style };
    const dimensions = {};
    if (props.height) dimensions.height = props.height;
    if (props.width) dimensions.width = props.width;

    return (
      // `display:block; width/height:100%` keeps the <picture> layout-transparent so the inner
      // <img>'s `height:100%`/`width:100%` still resolve against the original container (several
      // callers, e.g. the listing-wizard banner, rely on that cascade). Without it the inline
      // <picture> box would collapse the image.
      <picture style={{ display: 'block', width: '100%', height: '100%' }}>
        {/* Modern browsers pick this WebP source; Safari < 14 (11.1/12.1/13.1) can't decode
            image/webp, skips the source, and falls back to the JPEG <img> below. */}
        {srcset ? <source type="image/webp" srcSet={srcset} sizes={sizesAttr} /> : null}
        <img
          src={jpgDefaultUrl}
          srcSet={jpgSrcset}
          sizes={sizesAttr}
          alt={alt}
          loading={isCritical ? 'eager' : loading}
          decoding={isCritical ? 'sync' : decoding}
          fetchpriority={isCritical ? 'high' : fetchPriority}
          style={mergedStyles}
          className={cloudinaryClassName}
          {...dimensions}
        />
      </picture>
    );
  }

  if (image == null || variants?.length === 0) {
    const noImageClasses = classNames(rootClassName || css.root, css.noImageContainer, className);

    // const noImageMessageText = noImageMessage || <FormattedMessage id="ResponsiveImage.noImage" />;


    return (
      // <div className={noImageClasses}>
      //   <div className={css.noImageWrapper}>
      //     <NoImageIcon className={css.noImageIcon} />
      //     <div className={css.noImageText}>{noImageMessageText}</div>
      //   </div>
      // </div>
      <div className={noImageClasses}>
        <H4 className={css.noImage}>{abbreviatedName}</H4>
      </div>
    );
  }

  const imageVariants = image.attributes.variants;

  const srcSet = variants
    .map(variantName => {
      const variant = imageVariants[variantName];

      if (!variant) {
        // Variant not available (most like just not loaded yet)
        return null;
      }
      // Cloudinary-hosted variants (e.g. store-owner avatars uploaded as WebP) are forced to
      // JPEG so Safari < 14 (11.1/12.1/13.1) gets a decodable format — this account's f_auto
      // serves WebP regardless of the Accept header, which those versions can't decode. This is
      // a plain <img> (no <picture> WebP source), so a universally-decodable format is required.
      // No-op for Sharetribe-CDN variant URLs.
      return `${withCloudinaryJpgFormat(variant.url)} ${variant.width}w`;
    })
    .filter(v => v != null)
    .join(', ');

  const imgProps = {
    className: classes,
    srcSet,
    loading: isCritical ? 'eager' : loading,
    decoding: isCritical ? 'sync' : decoding,
    fetchpriority: isCritical ? 'high' : fetchPriority,
    ...rest,
  };

  return <img alt={alt} {...imgProps} onContextMenu={e => e.preventDefault()} />;
};

ResponsiveImage.defaultProps = {
  className: null,
  rootClassName: null,
  image: null,
  sizes: null,
  noImageMessage: null,
  isCritical: false,
  loading: 'lazy',
  decoding: 'async',
  fetchPriority: 'auto',
};

ResponsiveImage.propTypes = {
  className: string,
  rootClassName: string,
  alt: string.isRequired,
  image: oneOfType([propTypes.image, propTypes.imageAsset]),
  variants: arrayOf(string),
  sizes: string,
  noImageMessage: string,
  // Image optimization props
  isCritical: bool,
  loading: string,
  decoding: string,
  fetchPriority: string,
};

export default ResponsiveImage;